import { useTranslation } from 'react-i18next';

import { getIn } from 'formik';

import { Control, FormCard, RequiredSubtitle } from '../../../../components';
import { CARD_CATEGORIES } from '../../../../shared/cardConfig/constants';
import { contactInputEmpty, emptyOutFields } from '../../../../shared/cardConfig/helpers';
import { INVOICE_DELIVERY_FIELDS } from '../../../../shared/fieldConfig/constants';
import { clearFieldError } from '../../../../store/fieldError';
import { CONTROL_TYPE } from '../../../../utils/constants';
import { VALIDATION } from '../../../../utils/validation';
import type { CardApiService, CardDispatch, CardField, CardFieldProps, CardStore } from '../../types';
import CopyShipTo from '../CopyShipTo';

const category = CARD_CATEGORIES.invoiceDelivery;

function getEmailConfig({ name = '', t }: CardFieldProps) {
  return {
    field: 'email',
    category,
    label: t('shipTo.invoiceDelivery.email.label'),
    type: CONTROL_TYPE.inputWithTags,
    validations: [
      { type: VALIDATION.array, param: [{ type: VALIDATION.cardEmail }] },
      { type: VALIDATION.maxCharacters, param: 255 }
    ],
    onAddTag: ({ store, dispatch }: { store: CardStore; dispatch: CardDispatch }) => {
      // If there is an error on the parent ('invoice delivery') but an email has been entered, clear the error on the parent
      if (typeof store.fieldError[name] === 'string') {
        dispatch(clearFieldError(name));
      }
    },
    onChange: async (args: Parameters<typeof contactInputEmpty>[0]) => {
      contactInputEmpty(args, name, 'fax', '');
    },
    additionalProps: {
      placeholder: t('shipTo.invoiceDelivery.email.placeholder')
    }
  };
}

function getFaxConfig({ name = '', t }: CardFieldProps) {
  return {
    field: 'fax',
    category,
    label: t('shipTo.invoiceDelivery.fax.label'),
    type: CONTROL_TYPE.text,
    validations: [{ type: VALIDATION.numberOfDigits, param: 10 }],
    onBlur: ({ error, store, dispatch }: { error: unknown; store: CardStore; dispatch: CardDispatch }) => {
      // If there is an error on the parent ('invoiceDelivery') but no error on this field, clear the parent error
      if (!getIn(error, `${category}.fax`) && typeof store.fieldError[name] === 'string') {
        dispatch(clearFieldError(name));
      }
    },
    onChange: async (args: Parameters<typeof contactInputEmpty>[0]) => {
      contactInputEmpty(args, name, 'email', []);
    },
    additionalProps: {
      placeholder: t('shipTo.invoiceDelivery.fax.placeholder')
    }
  };
}

function getMailToBillToConfig({ name = '', t }: CardFieldProps) {
  return {
    field: 'mailToBillTo',
    category,
    label: t('shipTo.invoiceDelivery.mailToBillTo.label'),
    type: CONTROL_TYPE.checkbox,
    onChecked: async ({
      formik,
      dispatch,
      apiService,
      store
    }: {
      formik: { form: { errors: unknown; validateForm: () => void } };
      dispatch: CardDispatch;
      apiService: CardApiService;
      store: CardStore;
    }) => {
      await emptyOutFields(
        [
          { name: `${name}.email`, value: [] },
          { name: `${name}.fax`, value: '' }
        ],
        dispatch,
        formik
      );
      await apiService.updateCustomer(store.customer, dispatch);

      // When we are clearing out the other fields, formik is validating after each field
      // So when the final field is updated, to formik, in that moment invoiceDelivery looks like:
      // { email: [], fax: '', mailToBillTo: false}
      // Since we know this function runs when mailToBillTo is true we just ask formik to take another look
      if (getIn(formik.form.errors, name)) {
        formik.form.validateForm();
        dispatch(clearFieldError(name));
      }
    },
    additionalProps: {}
  };
}

function RequiredGroup({ isReview, t, index = -1, fields = [], fieldWrapper }: CardFieldProps) {
  let wrapper = '';
  // allows for fields to be included on the root level, nested object, or nested array
  // fieldWrapper may already have an index baked in (e.g. 'shipTo[0]'), so don't append a second one
  const isAlreadyIndexed = /\]$/.test(fieldWrapper || '');
  if (fieldWrapper) {
    wrapper = index > -1 && !isAlreadyIndexed ? `${fieldWrapper}[${index}].` : `${fieldWrapper}.`;
  }

  const name = `${wrapper}${category}`;

  const controlConfigMap: Record<string, (props: CardFieldProps) => Record<string, unknown>> = {
    [INVOICE_DELIVERY_FIELDS.email]: getEmailConfig,
    [INVOICE_DELIVERY_FIELDS.fax]: getFaxConfig,
    [INVOICE_DELIVERY_FIELDS.mailToBillTo]: getMailToBillToConfig
  };
  const config = {
    disabled: isReview,
    category,
    name,
    type: CONTROL_TYPE.requiredGroup,
    validations: [{ type: VALIDATION.requiredValidation }],
    subtitle: (
      <RequiredSubtitle text={t(`shipTo.invoiceDelivery.header`)} note={t(`shipTo.invoiceDelivery.headerSection`)} />
    ),
    requiredGroupFields: fields.map((fieldProps: CardField) => {
      const configFunc = controlConfigMap[fieldProps.key];
      return configFunc({ t, name, ...fieldProps });
    })
  };

  return <Control {...config} />;
}

interface InvoiceDeliveryContactCardProps {
  isReview?: boolean;
  index?: number;
  fields: CardField[];
  fieldWrapper?: string;
}

function InvoiceDeliveryContactCard({ isReview, index = -1, fields, fieldWrapper }: InvoiceDeliveryContactCardProps) {
  const { t } = useTranslation();

  const cardProps = {
    cardId: category,
    title: t('shipTo.invoiceDelivery.title'),
    subtitle: t('shipTo.invoiceDelivery.subtitle'),
    image: '/food/onboarding/assets/Invoice_Delivery.svg',
    isReview,
    fieldWrapper,
    t
  };

  return (
    <FormCard {...cardProps}>
      {!isReview && index > -1 && <CopyShipTo index={index} category={category} />}
      <RequiredGroup t={t} isReview={isReview} index={index} fields={fields} fieldWrapper={fieldWrapper} />
    </FormCard>
  );
}

export default InvoiceDeliveryContactCard;
